<template>
  <div>
    <div v-show="!showEditDialog">
      <LogBookTypeSelector
        v-if="!loading"
        :logtypes="logtypes"
        :logtype.sync="logtype"
        :logs="labeledLogs"
        :loading="loading"
      />

      <v-card class="mb-2 pa-2 card-rounded" flat>
        <v-card-text class="mb-2">
          <v-layout
            :column="isMobile"
            :align-center="!isMobile"
            style="gap: 4px"
          >
            <div v-if="!isMobile">
              <v-btn
                @click="$emit('newLog', true)"
                class="ml-0 mr-3 pr-3 pl-3"
                round
                large
                depressed
                color="primary"
              >
                <v-icon>add</v-icon>
                Novo
              </v-btn>
            </div>
            <div
              style="max-height: 48px"
              class="d-flex overflow-hidden card-rounded border grey--border"
            >
              <v-text-field
                class="mx-1"
                v-model="search"
                prepend-inner-icon="search"
                :label="$t('common.Search')"
                single-line
                hide-details
                solo
                flat
              />
              <v-divider vertical />
              <div
                @click="$root.$emit('drawer', true)"
                class="d-flex search-icon"
              >
                <v-badge
                  right
                  class="d-flex filter-number"
                  color="secondary"
                  :value="filterCount > 0 ? true : false"
                  overlap
                >
                  <template v-slot:badge>
                    <span>{{ filterCount }}</span>
                  </template>
                  <Icon size="28px" class="mx-2" name="filter" />
                </v-badge>
              </div>
            </div>

            <v-select
              label="Especialidade"
              class="thin-border"
              v-model="selectedSpecialty"
              :items="specialtyItems"
              :disabled="fetchingLogs"
              hideDetails
              single-line
              solo
              :style="isMobile ? '' : 'max-width: 300px'"
            />

            <v-layout justify-end align-items-center hidden-sm-and-down>
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn color="secondary" dark depressed small v-on="on">
                    Import/Export
                  </v-btn>
                </template>
                <v-list>
                  <ExcelExporter
                    :isButton="false"
                    title="logbooks"
                    label-field="text"
                    :entries="[{}]"
                    :components="excelComponents"
                    button-text="Exportar Template"
                  ></ExcelExporter>

                  <ExcelExporter
                    :isButton="false"
                    title="logbooks"
                    label-field="text"
                    :entries="completeLogs"
                    :components="excelComponents"
                    :buttonText="`Exportar ${completeLogs.length} logs`"
                  />

                  <v-list-tile
                    :loading="importLoading"
                    @click.native="selectFile"
                  >
                    <input
                      id="files"
                      ref="uploadInput"
                      type="file"
                      :multiple="false"
                      @change="detectFiles($event)"
                    />
                    <Icon name="upload" size="24px" class="mr-2" />
                    <v-list-tile-title>{{
                      $t('common.Import')
                    }}</v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-layout>
          </v-layout>
        </v-card-text>

        <v-divider />
        <portal to="sidebar">
          <v-card flat>
            <v-card-title>
              <Icon size="28px" class="mr-2" name="filter" />
              <h3 class="grey--text">Filtros:</h3>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <span class="caption grey--text">Tipo de logbook:</span>
              <v-select
                v-model="logtype"
                label="Logbook"
                item-value="id"
                item-text="name"
                single-line
                outline
                class="thin-border"
                :items="logtypes"
                :loading="loading"
              />
              <v-select
                v-model="internshipFilter"
                label="Estágio"
                class="thin-border"
                item-value="id"
                item-text="label"
                single-line
                solo
                clearable
                :items="selectableInternships"
                :disabled="fetchingLogs"
                :style="isMobile ? '' : 'max-width: 300px'"
              />
              <div class="mb-4">
                <span class="caption grey--text">Estado dos logs:</span>
                <v-btn-toggle
                  class="thin-border"
                  v-model="filterLogsByStatus"
                  mandatory
                >
                  <v-btn flat value="all">{{ $t('common.All') }}</v-btn>
                  <v-btn flat value="incomplete">{{
                    $t('common.Incomplete')
                  }}</v-btn>
                  <v-btn flat value="complete">{{
                    $t('common.Complete')
                  }}</v-btn>
                </v-btn-toggle>
              </div>
              <span class="caption grey--text">Periodo de tempo:</span><br />
              <DateRange
                :startDate.sync="startDate"
                :endDate.sync="endDate"
                singleDatePicker="range"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                round
                dark
                depressed
                color="secondary"
                @click="$root.$emit('drawer', false)"
              >
                Confirmar
              </v-btn>
            </v-card-actions>
          </v-card>
        </portal>

        <v-data-table
          v-model="selected"
          :loading="loading"
          :headers="headers"
          class="elevation-0"
          :items="displayableLogs"
          select-all
          :pagination.sync="pagination"
          :custom-sort="customSort"
        >
          <template v-slot:headers="props">
            <tr>
              <th>
                <v-checkbox
                  :input-value="props.all"
                  :indeterminate="props.indeterminate"
                  primary
                  hide-details
                  @click.stop="toggleAll"
                />
              </th>
              <th
                v-for="header in props.headers"
                style="text-align: center"
                :key="header.text"
                :class="[
                  'column sortable',
                  pagination.descending ? 'desc' : 'asc',
                  header.value === pagination.sortBy ? 'active' : ''
                ]"
                @click="changeSort(header.value)"
              >
                <v-icon small>arrow_upward</v-icon>
                {{ header.text }}
              </th>
            </tr>
          </template>
          <template v-slot:items="props">
            <tr :active="props.selected">
              <td>
                <v-checkbox v-model="props.selected" primary hide-details />
              </td>
              <template>
                <td
                  v-for="component in displayableComponents"
                  :key="component.id"
                  style="text-align: center"
                  @click="() => editLog(props.item.id)"
                >
                  <span
                    v-if="
                      component.type === 'checkbox' ||
                      typeof props.item[component.id] === 'boolean'
                    "
                    class="ml-3"
                  >
                    <v-icon
                      v-if="
                        props.item[component.id] &&
                        props.item[component.id].text === 'Sim'
                      "
                      small
                      >check</v-icon
                    >
                  </span>
                  <span v-else>{{
                    getCroppedText(props.item[component.id])
                  }}</span>
                </td>
              </template>
              <td style="text-align: center">
                <v-btn icon @click.stop="() => editLog(props.item.id)">
                  <Icon name="edit" size="16px" />
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:actions-prepend>
            <v-btn
              v-if="selected.length > 0"
              @click="$emit('shareLog', true)"
              depressed
              small
              dark
              class="mx-1"
              color="secondary white--text"
              style="flex: auto"
            >
              <v-icon small left class="mr-1">share</v-icon>
              Partilhar ({{ selected.length }})
            </v-btn>
            <v-btn
              v-if="selected.length > 0"
              @click="cloneSelected()"
              depressed
              small
              dark
              class="mx-1"
              color="secondary white--text"
              style="flex: auto"
            >
              <v-icon small left class="mr-1">content_copy</v-icon>
              Copiar ({{ selected.length }})
            </v-btn>
            <v-btn
              v-if="selected.length > 0"
              @click="showAlert = true"
              depressed
              small
              dark
              color="red white--text"
              class="pl-1 mx-1"
              style="flex: auto"
            >
              <v-icon small left class="mr-1">delete</v-icon>
              Apagar ({{ selected.length }})
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>
    <v-slide-y-reverse-transition>
      <v-card
        class="card-rounded"
        :class="isMobile ? '' : 'ma-3'"
        flat
        v-show="showEditDialog"
      >
        <LogForm
          v-if="logToEdit"
          :internship="internship"
          :log-to-edit="logToEdit"
          :close="closeEditLogForm"
          :selectedSpecialty="selectedSpecialty"
        />
      </v-card>
    </v-slide-y-reverse-transition>
    <v-dialog fullscreen :persistent="true" v-model="showLogbookImportDialog">
      <ImportLogbooksDialog
        :logtype="logtype"
        :components="excelComponents"
        :excel-header.sync="excelHeader"
        :excel-lines.sync="excelLines"
        :close-me="() => (showLogbookImportDialog = false)"
      />
    </v-dialog>
    <v-dialog v-model="showAlert" width="400">
      <v-card>
        <v-card-text>
          Ao apagar, estes registos não poderam ser recuperados.<br />
          Pretende apagar?
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small depressed @click="showAlert = false"> Cancelar </v-btn>
          <v-btn
            small
            depressed
            color="red white--text"
            @click="deleteSelected()"
          >
            Apagar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  Component,
  Option,
  Logbook,
  Internship
} from '../../../models/Specialty'
import ImportLogbooksDialog from '@/views/Logbook/components/ImportLogbooksDialog'
import LogForm from './LogForm'
import specialtyFetcher from '../../../mixins/specialtyFetcher'
import ExcelExporter from '@/components/data-exporter/ExcelExporter'
import DateRange from '../../Stats/DateRange.vue'
import excelHandling from '@/helpers/excel-handling'
import { UsersIdInfo } from '../../../models/UsersInfo'
import _ from 'lodash'
import batchSplit from '../../../utils/batchSplit'
import moment from 'moment'
import LogBookTypeSelector from './LogbookTypeSelector.vue'

export default {
  props: {
    requiredValidators: { type: Object, default: () => ({}) },
    internship: {
      type: String
    }
  },
  components: {
    ImportLogbooksDialog,
    LogForm,
    ExcelExporter,
    DateRange,
    LogBookTypeSelector
  },
  mixins: [specialtyFetcher],
  data() {
    return {
      showLogbookImportDialog: false,
      excelHeader: [],
      excelLines: [],
      logtype: '',
      today: new Date(),
      search: '',
      selected: [],
      pagination: {
        sortBy: 'createdAt',
        rowsPerPage: 10,
        descending: true
      },
      filterLogsByStatus: 'all',
      internshipFilter: undefined,
      logToEdit: null,
      showEditDialog: false,
      fetchingLogs: false,
      importLoading: false,
      startDate: '',
      endDate: '',
      selectedSpecialty: this.$store.getters.specialty,
      showAlert: false
    }
  },
  computed: {
    ...mapGetters(['user', 'internships', 'specialty']),
    selectableInternships() {
      const internshipOptions = Internship.getAvailableInternships(
        this.internships
      )

      return this.internships.map(i => {
        const found = internshipOptions.find(o => o.id === i.internship)
        if (found) {
          return {
            id: found.id,
            label: found.label
          }
        } else {
          return { id: i.internship, label: i.internship }
        }
      })
    },
    userNamesList() {
      const userNames = UsersIdInfo.query()
        .get()
        .filter(user => user.firstName !== undefined && !!user.license)
        .map(i => ({
          text: i.firstName + ' ' + i.lastName + ' (' + i.license + ')',
          value: i.id
        }))
      return userNames.length ? userNames : []
    },
    specialtyItems() {
      const internships = Internship.query().get()

      const specialties = [
        ...new Set(
          internships
            .filter(
              i =>
                (i.specialtyName &&
                  this.internships.some(item => item.internship === i.id)) ||
                i.specialtyId === this.specialty
            )
            .map(i => {
              return {
                value: i.specialtyId,
                text: i.specialtyName
              }
            })
        )
      ]

      return specialties
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    logbooks() {
      return Logbook.query().get()
    },
    options() {
      return Option.query().get()
    },
    logs() {
      return this.$store.getters.logs
    },
    labeledLogs() {
      // TODO: remove when logs have been migrated
      const logs = this.$store.getters.labeledLogs

      const isAnesthesiology = this.selectedSpecialty === 'anesthesiology'
      const isIntensiveMedicine =
        this.selectedSpecialty === 'intensive-medicine'

      return logs.filter(l => {
        const isTechniques = l.logbook === 'techniques'

        if (isAnesthesiology && isTechniques) {
          return !l.technique
        }

        if (isIntensiveMedicine && isTechniques) {
          return !l['type-of-technique']
        }

        return true
      })
    },
    displayableLogs() {
      let logs = this.labeledLogs
      if (this.search) {
        logs = this.searchIt(logs, this.search)
      }

      logs.map(log => {
        const displayableLog = { id: log.id }
        displayableLog.logbook = log.logbook
        displayableLog.internship = log.internship
        displayableLog.createdAt = log.createdAt
        this.displayableComponents.forEach(c => {
          displayableLog[c.id] = log[c.id]
        })

        displayableLog.incompleted = log.incompleted
        return displayableLog
      })

      if (this.startDate) {
        logs = logs.filter(
          l =>
            l['createdAt'] &&
            new Date(l['createdAt'].id) >= new Date(this.startDate)
        )
      }

      if (this.endDate) {
        logs = logs.filter(
          l =>
            l['createdAt'] &&
            new Date(l['createdAt'].id) <= new Date(this.endDate)
        )
      }
      if (this.internshipFilter) {
        logs = logs.filter(
          l => l.internship && l.internship.id === this.internshipFilter
        )
      }

      let filteredLogs = this.filters
        .filterLogsByStatus(logs, this.filterLogsByStatus)
        .filter(log => log.logbook === this.logtype)
      return filteredLogs
    },
    filters() {
      return {
        filterLogsByStatus: (logs, status) => {
          switch (status) {
            case 'complete':
              return logs.filter(log => !log.incompleted)
            case 'incomplete':
              return logs.filter(log => log.incompleted)
            default:
              return logs
          }
        }
      }
    },
    headers() {
      const headers = this.components
        .filter(c => c.show)
        .filter(c => c.logbooks && c.logbooks.includes(this.logtype))
        .map(({ label: text, id: value }) => ({
          text: this.$i18n.locale === 'pt' ? text : value,
          value,
          id: value
        }))
      headers.unshift({
        text: 'Data Criação',
        value: 'createdAt',
        id: 'createdAt'
      })
      headers.push({ text: 'Editar', value: 'edit', id: 'edit' })
      return headers
    },

    allComponents() {
      return this.logImportComponents.map(
        ({
          label: text,
          id: value,
          type: type,
          options: options,
          logbooks: logbooks,
          tabName: tabName
        }) => ({
          text: this.$i18n.locale === 'pt' ? text : value,
          value,
          id: value,
          type: type,
          options: options,
          logbooks: logbooks,
          tabName: tabName
        })
      )
    },
    excelComponents() {
      const components = this.allComponents
        .filter(c => c.logbooks.includes(this.logtype))
        .filter(c => c.type != 'image-upload')

      components.forEach(c => {
        if (this.multipleValuesComponent.some(mvc => mvc === c.type)) {
          c.text = `${c.text} (valores múltiplos)`
        }
      })

      return components
    },
    multipleValuesComponent() {
      return [
        'multi-select',
        'icdo-topography',
        'icdo-histology',
        'multi-select-tree'
      ]
    },
    completeLogs() {
      // TODO: remove side effect in computed prop
      if (!this.userNamesList.length) {
        this.getUserIdNames()
      }
      //
      const completeLogs = this.displayableLogs
        .filter(log => log.logbook === this.logtype)
        .map(log => {
          const logCpy = { id: log.id }
          this.components
            .filter(c => c.logbooks.includes(this.logtype))
            .forEach(component => {
              if (log[component.id]) {
                if (component.type === 'autonomy') {
                  logCpy[component.id] = this.getUserName(log, component)
                } else {
                  logCpy[component.id] =
                    log[component.id].text !== undefined &&
                    log[component.id].text !== null
                      ? log[component.id].text
                      : log[component.id]

                  //some ddb data is diff
                  if (logCpy[component.id].text) {
                    logCpy[component.id] = logCpy[component.id].text
                  }
                }
              }
            })
          return logCpy
        })
      return completeLogs
    },
    displayableComponents() {
      const components = this.components.filter(
        c => c.show && c.logbooks.includes(this.logtype)
      )
      components.unshift({
        id: 'createdAt',
        label: 'Data Criação',
        show: true
      })

      return components
    },

    components() {
      return Component.query().withAllRecursive().get() || []
    },
    logtypes() {
      return Logbook.query().get()
    },
    procedureOptions() {
      return Option.query()
        .where(
          'component',
          component =>
            component === 'anesthesia-procedure' ||
            component === 'general-anesthesia-type'
        )
        .get()
    },
    logImportComponents() {
      return this.components
        .filter(c => {
          return c.id !== 'logbook' && c.logbooks.includes(this.logtype)
        })
        .map(component => {
          if (component.id === 'loco-regional-anesthesia') {
            return { ...component, options: this.procedureOptions }
          } else return component
        })
    },
    requiredFields() {
      return this.components
        .filter(c => c.required && c.logbooks.includes(this.logtype))
        .map(c => c.id)
    },
    loading() {
      return this.fetchingLogs || this.components.length === 0
    },
    filterCount() {
      let total = 0
      this.internshipFilter ? (total += 1) : ''
      this.filterLogsByStatus !== 'all' ? (total += 1) : ''
      this.startDate ? (total += 1) : ''
      return total
    }
  },
  created() {
    if (!this.internships.length) {
      this.getInternships().catch(err =>
        this.showErrorSnackbar(err.message || 'Something went wrong')
      )
    }

    this.setLogs([])

    this.fetchingLogs = true
    this.fetchSpecialtyIfEmpty().then(() => {
      if (this.logtypes.length && !this.logtype)
        this.logtype = this.logtypes[0].id
      return this.getLogs()
        .then(() => {
          this.getLabeledLogs(this.selectedSpecialty)
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error(err)
          this.showErrorSnackbar('Something went wrong')
        })
        .finally(() => {
          this.fetchingLogs = false
        })
    })
    this.$root.$on('logbooktype', value => {
      this.logtype = value
    })
  },

  methods: {
    ...mapActions([
      'setLogs',
      'getLabeledLogs',
      'getLogs',
      'deleteLogs',
      'showSuccessSnackbar',
      'showErrorSnackbar',
      'getInternships'
    ]),
    fetchLogsAndSpecialty(specialty) {
      this.fetchingLogs = true
      this.setLogs([])
      this.internshipFilter = undefined

      this.fetchSpecialty(specialty, { seedInternships: false }).then(() => {
        if (this.logtypes.length) {
          this.logtype = this.logtypes[0].id
        }
        this.getLogs({ useCache: true })
          .catch(err =>
            this.showErrorSnackbar(err.message || 'Something went wrong')
          )
          .finally(() => {
            this.getLabeledLogs(specialty).then(() => {
              this.fetchingLogs = false
            })
          })
      })
    },
    searchIt(logs, searchKey) {
      // consider adding a MEGA string with all terms in the log object, do this in the store
      searchKey = searchKey
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
      const result = logs.filter(log => {
        return Object.keys(log).some(key => {
          if (typeof log[key] === 'object' && log[key] && log[key].text) {
            // excluding date and other objects from search
            const logValue = log[key].text
              .toString()
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
            return logValue.includes(searchKey)
          }
        })
      })
      return result
    },
    ageHandler(field) {
      if (field === '> 90') {
        return '91'
      } else if (isNaN(field)) {
        const regexpWithoutletters = /\b[0-9]+\b/gi
        const values = field
          .split(/([0-9]+)/)
          .join(' ')
          .match(regexpWithoutletters)

        return (
          values[0] +
          '.' +
          ('0' + values[1]).slice(-2) +
          ('0' + values[2]).slice(-2)
        )
      }
      return field
    },
    customSort(logs, index, isDesc) {
      if (logs) {
        if (index) {
          const direction = isDesc ? -1 : 1
          logs.sort((a, b) => {
            if (!a[index] || !a[index].text) {
              return direction * -1
            } else if (!b[index] || !b[index].text) {
              return direction * 1
            } else if (index === 'entry-date' || index === 'createdAt') {
              return (
                direction *
                (moment(a[index].text, 'DD/MM/YYYY').toDate() <
                moment(b[index].text, 'DD/MM/YYYY').toDate()
                  ? -1
                  : 1)
              )
            } else if (index === 'age') {
              return (
                direction *
                (this.ageHandler(a[index].text) -
                  this.ageHandler(b[index].text))
              )
            } else {
              return direction * (a[index].text < b[index].text ? -1 : 1)
            }
          })
        }
      }
      return logs
    },
    async getUserIdNames() {
      let UserIdsList = []
      // UserIdsList = for each selected log get Ids from autonomy components
      const Ids = this.logs
        .filter(log => log.logbook === this.logtype)
        .map(log => {
          return this.getIds(log)
        })
      UserIdsList = _.uniqBy(Ids.reduce((acc, cur) => [...acc, ...cur], []))
      //  Get User names
      if (UserIdsList.length) {
        // the Firebase only suports a range of 10 elements
        const batchesUsers = batchSplit(UserIdsList, 10)
        await UsersIdInfo.fetch(
          this.$store.getters.accessToken,
          batchesUsers
        ).catch(err => {
          return this.$store.dispatch(
            'showErrorSnackbar',
            err.message || 'Something went wrong'
          )
        })
      }
    },

    getIds(log) {
      const logIds = this.components
        .filter(c => c.logbooks.includes(this.logtype) && c.type === 'autonomy')
        .map(c => {
          if (log[c.id]) {
            return log[c.id]
          }
        })
      return logIds.filter(log => log)
    },

    toggleAll() {
      if (this.selected.length) this.selected = []
      else this.selected = this.displayableLogs.slice()
    },
    getUserName(log, component) {
      let userInfo = null
      if (log[component.id]) {
        userInfo = this.userNamesList.find(
          l => l.value === log[component.id].text
        )
      }
      return userInfo ? userInfo.text : log[component.id].text
    },
    getCroppedText(item) {
      if (!item || !item.text) return ''
      if (!isNaN(item.text)) return item.text
      return item.text.length < 50
        ? item.text
        : `${item.text.toString().substring(0, 49)}...`
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    deleteSelected() {
      if (this.selected.length) {
        this.deleteLogs(this.selected)
          .then(() => this.showErrorSnackbar(this.$t('common.EntryDeleted')))
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error(err)
            this.showErrorSnackbar(err.message || 'Something went wrong')
          })
        this.showAlert = false
      }
    },
    cloneSelected() {
      if (this.selected.length === 1) {
        this.cloneAndEditLog(this.selected[0])
      } else {
        this.showErrorSnackbar('Por favor selecione uma entrada.')
      }
    },
    shareSelected() {
      const selectedRows = this.selected.map(row => {
        const completeRow = this.logs.find(
          completeLog => completeLog.id === row.id
        )
        return completeRow
      })
      if (selectedRows.length < 1) {
        this.showErrorSnackbar('Por favor selecione registo(s) a partilhar.')
      } else {
        return selectedRows
      }
    },
    closeEditLogForm() {
      this.showEditDialog = false
      this.logToEdit = null
    },
    editLog(logId) {
      this.logToEdit = this.logs.find(l => l.id === logId)
      this.showEditDialog = true
    },
    cloneAndEditLog(selection) {
      const log = this.logs.find(l => l.id === selection.id)

      const clone = { ...log }
      delete clone.id
      delete clone._id
      delete clone['entry-date']

      this.logToEdit = clone
      this.showEditDialog = true
    },
    selectFile() {
      this.$refs.uploadInput.click()
    },
    detectFiles(e) {
      let fileList = e.target.files || e.dataTransfer.files

      if (!fileList || !fileList.length) {
        this.importLoading = false
      } else {
        this.importLoading = true
        this.parse(fileList[0])
      }
    },
    parse(file) {
      excelHandling
        .readExcel(file)
        .then(data => {
          const excelHeader = data.shift()

          const maxEntries = 600

          if (data.length > maxEntries) {
            throw new Error(
              `O número máximo de linhas a importar é ${maxEntries}`
            )
          }

          excelHeader.forEach(h => {
            if (!this.excelComponents.some(c => h.includes(c.text))) {
              throw new Error(
                'Template de excel não válido para estágio selecionado'
              )
            }
          })

          this.excelHeader = excelHeader
          this.excelLines = data

          this.showLogbookImportDialog = true
          this.$refs.uploadInput.value = null
        })
        .catch(e => {
          this.$store.dispatch(
            'showErrorSnackbar',
            e.message || 'Something went wrong'
          )
        })
        .finally(() => {
          this.importLoading = false
        })
    }
  },
  watch: {
    logtypes(logtypes) {
      if (!this.logtype && logtypes.length) {
        this.logtype = logtypes[0].id
      }
    },
    selectedSpecialty(curr, prev) {
      if (curr && prev) {
        this.fetchLogsAndSpecialty(curr)
      }
    }
  }
}
</script>

<style lang="css">
td span {
  white-space: pre-line;
}
.search-icon {
  max-width: 50px;
  cursor: pointer;
}
.search-icon:hover {
  background-color: #f1f1f1;
  cursor: pointer;
}
.filter-number .v-badge__badge {
  top: 2px !important;
  right: 2px !important;
  border: 2px solid #fff !important;
  height: 18px;
  width: 18px;
}
.filter-number .v-badge__badge span {
  position: relative;
  top: 0px;
  font-weight: bold;
  font-size: 10px;
}
.v-datatable__actions > div:first-child {
  flex: auto;
}

.v-datatable__actions__select {
  width: min-content;
}
</style>
